// yard/DogManager.jsx - Manages all dog instances with LOD, culling, and smart labels
import React, { useMemo, useCallback } from 'react';
import { useThree } from '@react-three/fiber';
import useYardStore from './yardStore';
import DogInstance from './DogInstance';

const DogManager = ({ darkMode, onDogSelect }) => {
  const { camera } = useThree();
  const dogs = useYardStore(state => state.dogs);
  const filters = useYardStore(state => state.filters);
  const performance = useYardStore(state => state.performance);
  const focusOnDog = useYardStore(state => state.focusOnDog);
  const selectedDogId = useYardStore(state => state.selectedDogId);

  // Filter dogs based on active filters
  const filteredDogs = useMemo(() => {
    return dogs.filter(dog => {
      if (filters.owner !== 'all' && dog.deal?.owner !== filters.owner) return false;
      if (filters.stage !== 'all' && dog.deal?.stage !== filters.stage) return false;
      if (filters.state !== 'all' && dog.dealState !== filters.state) return false;
      return true;
    });
  }, [dogs, filters]);

  // Sort dogs by distance to camera for rendering priority
  const sortedDogs = useMemo(() => {
    const camPos = camera.position;
    return [...filteredDogs]
      .map(dog => ({
        ...dog,
        distance: Math.sqrt(
          Math.pow(dog.position.x - camPos.x, 2) +
          Math.pow(dog.position.z - camPos.z, 2)
        )
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, performance.maxVisibleDogs);
  }, [filteredDogs, camera.position, performance.maxVisibleDogs]);

  // Proximity-based label visibility: only show labels for dogs with enough spacing
  const labelVisibility = useMemo(() => {
    const visibleLabels = new Set();
    const MIN_LABEL_DISTANCE = 3.5;

    for (const dog of sortedDogs) {
      if (dog.distance > 25) continue;

      let tooClose = false;
      for (const labeledId of visibleLabels) {
        const labeled = sortedDogs.find(d => d.id === labeledId);
        if (!labeled) continue;
        const dx = dog.position.x - labeled.position.x;
        const dz = dog.position.z - labeled.position.z;
        if (Math.sqrt(dx * dx + dz * dz) < MIN_LABEL_DISTANCE) {
          tooClose = true;
          break;
        }
      }

      if (!tooClose) {
        visibleLabels.add(dog.id);
      }
    }

    return visibleLabels;
  }, [sortedDogs]);

  // Handle dog click - focus camera AND show modal
  const handleDogClick = useCallback((dog) => {
    focusOnDog(dog.id);
    if (onDogSelect && dog.deal) {
      onDogSelect(dog.deal);
    }
  }, [focusOnDog, onDogSelect]);

  return (
    <group>
      {sortedDogs.map(dog => {
        // LOD based on distance
        const lod = dog.distance > performance.lodDistance ? 'low'
                  : dog.distance > 25 ? 'medium'
                  : 'high';

        // When a dog is selected (modal open), only show that dog's label and dim others
        const showLabel = selectedDogId
          ? dog.id === selectedDogId
          : labelVisibility.has(dog.id);

        const dimmed = selectedDogId && dog.id !== selectedDogId;

        return (
          <DogInstance
            key={dog.id}
            dog={dog}
            onClick={handleDogClick}
            showLabel={showLabel}
            dimmed={dimmed}
            lod={lod}
          />
        );
      })}
    </group>
  );
};

export default DogManager;
